import React, { createContext, useContext, useMemo, useRef } from 'react';

import { useItineraryComponents } from '../use-itineraries';

import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * Who may read absolute money on the Days / Preview cards, and what each
 * component cost when the screen first loaded.
 *
 * Only ADMIN sees absolute amounts. Every other role sees a +/- delta against
 * the baseline, and nothing at all when the price has not moved - the operator
 * learns that a swap raised the fare by 100 without learning the fare.
 *
 * The baseline is session-scoped by design. It is captured from the first
 * server payload after mount and never rewritten, so a swap or a "Fetch latest
 * prices" run keeps showing its delta for as long as the operator stays on the
 * screen. A reload re-baselines to whatever is now saved and the deltas clear.
 * `ItineraryComponentOut` carries a single `costPaise` and no baseline column,
 * so there is nothing to persist a delta against across reloads.
 */
interface PriceVisibility {
  /** ADMIN only. Gates every absolute amount on the day cards and the picker. */
  canSeeAmounts: boolean;
  /** costPaise as of the first load this session; null for items added since. */
  baselineFor: (componentId: string) => string | null;
}

/**
 * Defaults to full visibility so a card rendered outside the provider (tests,
 * a future surface) fails open rather than silently blanking every price.
 */
const PriceVisibilityContext = createContext<PriceVisibility>({
  canSeeAmounts: true,
  baselineFor: () => null,
});

export const usePriceVisibility = (): PriceVisibility => useContext(PriceVisibilityContext);

export const PriceVisibilityProvider = ({
  itineraryId,
  children,
}: {
  itineraryId: string;
  children: React.ReactNode;
}): React.JSX.Element => {
  const canSeeAmounts = useCurrentUser()?.role === 'ADMIN';
  const serverComponents = useItineraryComponents(itineraryId).data;
  const baselines = useRef(new Map<string, string>());

  // First sighting of an id wins. Autosave round-trips and refetches must not
  // move the baseline out from under a delta the operator is looking at. The
  // write is idempotent, so a StrictMode double-render is harmless.
  for (const c of serverComponents ?? []) {
    if (!baselines.current.has(c.id)) baselines.current.set(c.id, c.costPaise);
  }

  const value = useMemo<PriceVisibility>(
    () => ({
      canSeeAmounts,
      baselineFor: (id) => baselines.current.get(id) ?? null,
    }),
    [canSeeAmounts],
  );

  return (
    <PriceVisibilityContext.Provider value={value}>{children}</PriceVisibilityContext.Provider>
  );
};

// ---- Formatting ----

export const fmtMoney = (paise?: string | null, currency = 'INR'): string | null => {
  if (!paise) return null;
  const n = Number(paise);
  if (!Number.isFinite(n) || n === 0) return null;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n / 100);
  } catch {
    return `${currency} ${Math.round(n / 100).toLocaleString('en-IN')}`;
  }
};

/** Signed paise moved since `base`, or null when there's no base or no change. */
const deltaPaise = (base?: string | null, now?: string | null): number | null => {
  if (!base || !now) return null;
  const d = Number(now) - Number(base);
  return Number.isFinite(d) && d !== 0 ? d : null;
};

/**
 * A price movement, rendered as "+₹100" / "-₹250".
 *
 * Colour follows the operator's convention, not the finance one: a "+" reads
 * emerald and a "-" reads rose. Read it as "the plus is the number you add",
 * not as "green means good" - a rise in cost is not good news.
 */
export const DeltaBadge = ({
  paise,
  currency,
  title,
}: {
  paise: number;
  currency: string;
  title?: string;
}): React.JSX.Element => {
  const up = paise > 0;
  return (
    <span
      className={
        'text-[13px] font-semibold tabular-nums ' + (up ? 'text-emerald-600' : 'text-rose-600')
      }
      title={
        title ??
        (up ? 'Costlier than when this screen loaded' : 'Cheaper than when this screen loaded')
      }
    >
      {up ? '+' : '-'}
      {fmtMoney(String(Math.abs(paise)), currency)}
    </span>
  );
};

/**
 * Price difference of a candidate option against the option currently chosen,
 * shown so the operator sees whether a swap raises or lowers the fare. Renders
 * nothing when there is no current selection or the price is identical.
 */
export const PriceDelta = ({
  base,
  now,
  currency,
}: {
  base?: string | null;
  now: string;
  currency: string;
}): React.JSX.Element | null => {
  const d = deltaPaise(base, now);
  if (d === null) return null;
  return (
    <DeltaBadge
      paise={d}
      currency={currency}
      title={d > 0 ? 'Costlier than the current pick' : 'Cheaper than the current pick'}
    />
  );
};

/**
 * The money block on a day card. Admin gets the amount, its caption, and a
 * delta badge when the price moved this session. Everyone else gets the delta
 * badge alone - and an empty block when nothing moved.
 */
export const CardPrice = ({
  componentId,
  costPaise,
  currency,
  amountClassName,
  caption,
  captionClassName = 'mt-0.5 text-[11px] text-zinc-400',
}: {
  componentId: string;
  costPaise?: string | null;
  currency: string;
  amountClassName: string;
  caption?: string;
  captionClassName?: string;
}): React.JSX.Element | null => {
  const { canSeeAmounts, baselineFor } = usePriceVisibility();
  const delta = deltaPaise(baselineFor(componentId), costPaise);
  const deltaBadge = delta === null ? null : <DeltaBadge paise={delta} currency={currency} />;

  if (!canSeeAmounts) return deltaBadge;

  // A cost of 0 renders no amount (fmtMoney returns null), but it may still be
  // a drop from a non-zero baseline - keep the badge so the move isn't lost.
  const amount = fmtMoney(costPaise, currency);
  if (!amount) return deltaBadge;
  return (
    <>
      <span className={amountClassName}>{amount}</span>
      {caption && <p className={captionClassName}>{caption}</p>}
      {deltaBadge}
    </>
  );
};

/**
 * An absolute amount with no delta fallback - used for picker rows, whose
 * candidate components have no baseline (they are not in the itinerary yet).
 * Non-admins see nothing; the row's own `PriceDelta` carries the signal.
 */
export const AdminOnlyAmount = ({
  costPaise,
  currency,
  className,
}: {
  costPaise?: string | null;
  currency: string;
  className: string;
}): React.JSX.Element | null => {
  const { canSeeAmounts } = usePriceVisibility();
  if (!canSeeAmounts) return null;
  const amount = fmtMoney(costPaise, currency);
  if (!amount) return null;
  return <span className={className}>{amount}</span>;
};
